import { NewModal, Search } from 'lib/components';
import React, { useState } from 'react';
import { SecondaryLabel } from '../components/form/styles';
import { useQueryGetAllGroups } from 'lib/api/superadmin/landingPageDesignGroups/useQueryGetAllGroups';
import styled, { css, useTheme } from 'styled-components/macro';
import { Flex, Gap } from 'lib/components/styles/layout';
import { Button } from 'react-covideo-common';
import { IoMdCloseCircle } from 'react-icons/io';
import { CREATE_NEW_GROUP_ID } from '../constants';
import { useCreateGroupMutation } from 'lib/api/superadmin/landingPageDesignGroups/useCreateGroupMutation';
import { useAddBatchToGroup } from 'lib/api/superadmin/landingPageDesignGroups/useAddBatchToGroups';
import { ILandingPageDesignResponse } from '../types';

type SearchResultsProps = {
  searchResultLength: number;
};

const SearchResultsWrapper = styled.div<SearchResultsProps>`
  ${props =>
    !!props.searchResultLength &&
    css`
      border-bottom-right-radius: 6px;
      border-bottom-left-radius: 6px;
      border: 1px solid ${({ theme }) => theme.colors.neutral[40]};
      max-height: 80px;
    `}
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  margin-bottom: 8px;

  position: absolute;
  top: 73px;
  left: 0;
  width: calc(100% - 2px);
  background-color: ${({ theme }) => theme.colors.white[100]};
`;

const SearchResultItem = styled.div`
  padding: 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.colors.primary[100]};

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary[5]};
  }
`;

type AddToGroupModalProps = {
  handleCloseModal: () => void;
  selectedLandingPageDesigns: ILandingPageDesignResponse[];
};

type GroupSearchOption = {
  value: number;
  label: string;
};

const AddToGroupModal = ({
  handleCloseModal,
  selectedLandingPageDesigns,
}: AddToGroupModalProps) => {
  const isSingularLandingPage = selectedLandingPageDesigns.length === 1;

  const [searchResults, setSearchResults] = useState<GroupSearchOption[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<GroupSearchOption[]>(
    isSingularLandingPage
      ? selectedLandingPageDesigns[0].groups.map(group => ({
          value: group.landingPageDesignGroupId,
          label: group.title,
        }))
      : []
  );
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const theme = useTheme();
  const { data } = useQueryGetAllGroups();
  const { mutateAsync: createGroup, isLoading: isCreatingGroup } =
    useCreateGroupMutation({
      shouldShowNotifications: false,
      shouldInvalidateLandingPages: false,
    });

  const {
    mutateAsync: addBatchToGroups,
    isLoading: isAddingLandingPagesToGroup,
  } = useAddBatchToGroup(handleCloseModal);

  const handleSearch = (searchValue: string) => {
    // open menu
    setMenuIsOpen(true);

    if (!searchValue) {
      setSearchResults([]);
      return;
    }
    const updatedSearchResults = (data || [])
      .filter(group =>
        group.title.toLowerCase().includes(searchValue.toLowerCase().trim())
      )
      .map(group => ({
        value: group.landingPageDesignGroupId,
        label: group.title,
      }));

    // if search results are empty, add option to create group
    if (!updatedSearchResults.length) {
      updatedSearchResults.push({
        value: CREATE_NEW_GROUP_ID,
        label: searchValue.trim(),
      });
    }
    setSearchResults(updatedSearchResults);
  };

  const handleSelectGroup = async (group: GroupSearchOption) => {
    if (group.value === CREATE_NEW_GROUP_ID) {
      // triger create mutation
      const newGroup = await createGroup({
        title: group.label,
        landingPageDesigns: [],
      });

      // add new group to list of selected groups
      setSelectedGroups(prev => [
        ...prev,
        {
          label: newGroup.title,
          value: newGroup.landingPageDesignGroupId,
        },
      ]);

      // close menu
      setMenuIsOpen(false);
      return;
    }

    const exists = !!selectedGroups.find(
      selectedGroup => selectedGroup.value === group.value
    );
    if (exists) {
      // remove
      setSelectedGroups(prev =>
        prev.filter(selectedGroup => selectedGroup.value !== group.value)
      );
    } else {
      setSelectedGroups(prev => [...prev, group]);
    }

    // close menu
    setMenuIsOpen(false);
  };

  const handleRemoveGroup = (groupId: number) => {
    setSelectedGroups(prev =>
      prev.filter(selectedGroup => selectedGroup.value !== groupId)
    );
  };

  const handleAddToGroup = async () => {
    await addBatchToGroups({
      groupIds: selectedGroups.map(selectedGroup => selectedGroup.value),
      landingPageDesigns: selectedLandingPageDesigns.map(
        lpDesign => lpDesign.id
      ),
    });
  };

  const handleClickSearchBar = () => {
    setMenuIsOpen(!menuIsOpen);
    if (!!menuIsOpen) return;
    const searchRes = (data || []).map(group => ({
      value: group.landingPageDesignGroupId,
      label: group.title,
    }));
    setSearchResults(searchRes);
  };

  const isSubmitting = isCreatingGroup || isAddingLandingPagesToGroup;
  const isDisabled = isSubmitting || !selectedGroups.length;

  return (
    <NewModal
      closeModal={handleCloseModal}
      headerText={
        isSingularLandingPage
          ? `Manage Groups - ${selectedLandingPageDesigns[0].title}`
          : 'Add to Group'
      }
      style={{
        content: {
          height: '300px',
        },
      }}
    >
      <Flex gap='20px' height='100%'>
        {isSingularLandingPage && (
          <div>Add this template to one or more groups.</div>
        )}
        <Flex gap='8px' height='100%' justifyContent='space-between'>
          <Flex style={{ position: 'relative' }}>
            <Gap onClick={handleClickSearchBar}>
              <SecondaryLabel
                style={{ fontSize: '14px' }}
              >{`Select Group(s)`}</SecondaryLabel>
              <Search
                onChange={e => handleSearch(e.target.value)}
                onSearch={value => handleSearch(value)}
                placeholder='Search Groups…'
                disabled={isSubmitting}
              />
            </Gap>

            {menuIsOpen && (
              <SearchResultsWrapper searchResultLength={searchResults.length}>
                {searchResults.map(group => (
                  <SearchResultItem
                    key={`group-${group.value}`}
                    onClick={() => {
                      handleSelectGroup(group);
                    }}
                  >
                    {group.value === CREATE_NEW_GROUP_ID
                      ? `+ Create "${group.label}"`
                      : group.label}
                  </SearchResultItem>
                ))}
              </SearchResultsWrapper>
            )}

            <Flex
              flexDirection='row'
              gap='5px'
              flexWrap='wrap'
              margin='10px 0'
              width='592px'
            >
              {selectedGroups.map(selectedGroup => (
                <Button
                  text={selectedGroup.label}
                  variant='secondary'
                  size='small'
                  shape='rounded'
                  icon={<IoMdCloseCircle color={theme.colors.secondary[40]} />}
                  iconPosition='right'
                  onClick={() => handleRemoveGroup(selectedGroup.value)}
                />
              ))}
            </Flex>
          </Flex>

          <Flex gap='8px' flexDirection='row' justifyContent='flex-end'>
            <Button
              text='Cancel'
              variant='secondary'
              onClick={handleCloseModal}
            />
            <Button
              text={'Add To Group'}
              onClick={handleAddToGroup}
              disabled={isDisabled}
              loading={isCreatingGroup}
            />
          </Flex>
        </Flex>
      </Flex>
    </NewModal>
  );
};

export default AddToGroupModal;
